<template>
	<ConfirmModal
		v-model="showModal"
		confirmTitle="Your session is about to end."
		:confirmMessage="getGenericContent('site-timeout-message')"
		confirmActionText="Sign out"
		cancelActionText="Stay signed in"
		:confirmAction="logoutCta"
		:cancelAction="refreshSession"
	/>
</template>

<script lang="ts" setup>
// ====---------------====
// 🚀 Imports
// ====---------------====
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import ConfirmModal from './ConfirmModal.vue'
import { logout, refreshAuthToken } from '@/services/api/apiAuthHelpers.ts'
import { useRouter } from 'vue-router'
import { getGenericContent } from '@/services/cms/utils.ts'
const router = useRouter()

// ====---------------====
// 💡 Data
// ====---------------====
const timeoutDuration = computed<number>(
	() => (import.meta.env.VITE_ENV_MODE == 'local' ? 99 : 12) * 60 * 1000
)
const autoLogoutDuration = computed<number>(() => 3 * 60 * 1000)
const showModal = ref<boolean>(false)
const warningTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const logoutTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const logoutUser = ref<boolean>(false)

// ====---------------====
// 🛠 Methods
// ====---------------====
function logoutCta() {
	showModal.value = false
	logoutUser.value = true
	logout()
	nextTick()
	router.push('/login')
}

function setSessionTimeout() {
	if (warningTimeout.value) clearTimeout(warningTimeout.value)
	if (logoutTimeout.value) clearTimeout(logoutTimeout.value)

	if (sessionStorage.getItem('access_token') != null) {
		warningTimeout.value = setTimeout(() => {
			showModal.value = true
			logoutTimeout.value = setTimeout(() => {
				logoutCta()
			}, autoLogoutDuration.value)
		}, timeoutDuration.value)
	}
}

function refreshTimeout() {
	setSessionTimeout()
}

async function refreshSession() {
	refreshTimeout()
	await refreshAuthToken()
}

// ====---------------====
// 🌄 Lifecycle
// ====---------------====

// add listener when instance is created
window.addEventListener('user-activity', refreshTimeout)

window.addEventListener('focus', () => {
	nextTick()
	if (logoutUser.value) logoutCta()
})

onBeforeUnmount(() => {
	window.removeEventListener('user-activity', refreshTimeout)
})
</script>

<style lang="scss" scoped></style>
